import React, { useState, useEffect } from "react";
import { Eye, ChevronDown, Calculator, X } from "lucide-react";
import { toast } from "react-toastify";

const formatNumberIN = (value: number): string => {
  const safeValue = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeValue);
};

const formatINR = (value: number): string => `₹${formatNumberIN(value)}`;

const UOM_CONVERSION_TO_GRAM: Record<string, number> = {
  'g': 1,
  'kg': 1000,
};

const getPurityFromSources = (...sources: any[]): string => {
  for (const source of sources) {
    if (source === null || source === undefined) continue;

    if (typeof source !== "object") {
      const value = String(source).trim();
      if (value) return value;
      continue;
    }

    const candidates = [
      source.purity,
      source.Purity,
      source.requirement?.Purity,
      source.requirement?.purity,
      source.matchedItem?.Purity,
      source.matchedItem?.purity,
    ];

    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) continue;
      const value = String(candidate).trim();
      if (value) return value;
    }
  }

  return "";
};

interface DealCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRow: any;
  metalList: any[];
  getBuyerInfo: (userId: number | null) => { name: string; phone: string };
  getMetalName: (id?: number) => string;
  getPurityLabel: (metalTypeId: number, categoryName: string) => string;
  acceptedSellers?: any[];
}

const DealCalculatorModal: React.FC<DealCalculatorModalProps> = ({
  isOpen,
  onClose,
  selectedRow,
  metalList,
  getBuyerInfo,
  getMetalName,
  getPurityLabel,
  acceptedSellers = []
}) => {
  // State declarations
  const [selectedMetalSeller, setSelectedMetalSeller] = useState<string>("");
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null);
  const [showSellerDropdown, setShowSellerDropdown] = useState(false);
  const [selectedMetalType, setSelectedMetalType] = useState<string>("");
  const [showMetalTypeDropdown, setShowMetalTypeDropdown] = useState(false);
  const [priceInput, setPriceInput] = useState<string>("");
  const [selectedUOM, setSelectedUOM] = useState<string>("");
  const [selectedUOMId, setSelectedUOMId] = useState<number | null>(null);
  const [showUOMDropdown, setShowUOMDropdown] = useState(false);
  const [uomOptions, setUomOptions] = useState<any[]>([]);
  const [priceVisible, setPriceVisible] = useState(true);
  const [purityValue, setPurityValue] = useState<string>("");
  const [purityInput, setPurityInput] = useState<string>("");
  const [showKaratDropdown, setShowKaratDropdown] = useState(false);
  const [showCentDropdown, setShowCentDropdown] = useState(false);
  const [selectedKarat, setSelectedKarat] = useState<string>("");
  const [selectedCent, setSelectedCent] = useState<string>("");
  const [purityLabel, setPurityLabel] = useState<string>("Purity");
  const [showWastage, setShowWastage] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<string>("");
  const [showArticleDropdown, setShowArticleDropdown] = useState(false);
  const [weightPerPieceGrams, setWeightPerPieceGrams] = useState<number>(0);
  const [originalWeight, setOriginalWeight] = useState<string>("");
  const [originalUOM, setOriginalUOM] = useState<string>("");
  const [showT2TSection, setShowT2TSection] = useState(false);
  const [t2tOption, setT2tOption] = useState<string>("no");
  const [difference, setDifference] = useState<number>(0);
  const increaseDifference = () => setDifference(prev => +(prev + 0.1).toFixed(1));
  const decreaseDifference = () => setDifference(prev => +(prev - 0.1).toFixed(1));
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [wastageInput, setWastageInput] = useState<string>("");
  const [makingChargesInput, setMakingChargesInput] = useState<string>("");
  const [commissionInput, setCommissionInput] = useState<string>("");
  const [gstInput, setGstInput] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("COD");
  const [marginMakingChargesInput, setMarginMakingChargesInput] = useState<string>("");
  const [originalTunchSeller, setOriginalTunchSeller] = useState<number>(0);
  const [fineValue, setFineValue] = useState<number>(0);
  const [calculatedPriceValue, setCalculatedPriceValue] = useState<number>(0);
  const [sellerGstAmount, setSellerGstAmount] = useState<number>(0);
  const [sellerGrandTotal, setSellerGrandTotal] = useState<number>(0);
  const [originalTunchBuyer, setOriginalTunchBuyer] = useState<number>(0);
  const [fineValueBuyer, setFineValueBuyer] = useState<number>(0);
  const [calculatedPriceValueBuyer, setCalculatedPriceValueBuyer] = useState<number>(0);
  const [perGramPrice, setPerGramPrice] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [subtotal, setSubtotal] = useState<number>(0);
  const [commissionAmount, setCommissionAmount] = useState<number>(0);
  const [gstAmount, setGstAmount] = useState<number>(0);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [marginMakingChargesProfit, setMarginMakingChargesProfit] = useState<number>(0);
  const [matchedItemData, setMatchedItemData] = useState<any>(null);
  const [currentSelectedSellerData, setCurrentSelectedSellerData] = useState<any>(null);
  const [isPurityEdited, setIsPurityEdited] = useState(false);
  const [isKaratEdited, setIsKaratEdited] = useState(false);
  const [isMakingChargesEdited, setIsMakingChargesEdited] = useState(false);
  const [uniquenumber, setuniquenumber] = useState<string>("");

  const userData = localStorage.getItem("user");
  const currentUser = userData ? JSON.parse(userData) : null;
  const currentUserRoleId = currentUser?.role_id || null;
  const isSuperAdmin = currentUserRoleId === "1";

  const [editableQuantity, setEditableQuantity] = useState<string>("");

  // Fetch UOMs from API
  useEffect(() => {
    const fetchUOMs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://api.sonachandiwale.com/api/v1/admin/get_All_UOM', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.data) {
          setUomOptions(data.data);
        }
      } catch (error) {
        console.error("Error fetching UOMs:", error);
        setUomOptions([
          { id: 1, name: 'g' },
          { id: 2, name: 'kg' }
        ]);
      }
    };
    fetchUOMs();
  }, []);

  // UOM Change Handler - updates both states and triggers re-calculation
  const handleUOMChange = (uom: any) => {
    const uomName = uom.name || uom.uomName || uom.unit || `UOM ${uom.id}`;
    setSelectedUOM(uomName);
    setSelectedUOMId(uom.id);
    setOriginalUOM(uomName);
    setShowUOMDropdown(false);
    
    // Convert weight based on new UOM
    const weightNum = parseFloat(originalWeight) || 0;
    const convertedWeight = convertToGrams(weightNum, uomName);
    setWeightPerPieceGrams(convertedWeight);
  };

  const convertToGrams = (weight: number, fromUOM: string): number => {
    const uom = fromUOM?.toLowerCase() || 'g';
    const conversionFactor = UOM_CONVERSION_TO_GRAM[uom] || 1;
    return weight * conversionFactor;
  };

  // Update per‑piece weight in grams
  useEffect(() => {
    const weightNum = parseFloat(originalWeight) || 0;
    const convertedWeight = convertToGrams(weightNum, originalUOM || selectedUOM || 'g');
    setWeightPerPieceGrams(convertedWeight);
  }, [originalWeight, originalUOM, selectedUOM]);

  // Making charges and margin making charges are multiplied by quantity
  const quantityNum = parseFloat(editableQuantity) || 0;
  const makingChargesAmount = (parseFloat(makingChargesInput) || 0) * quantityNum;
  const marginMakingChargesAmount = (parseFloat(marginMakingChargesInput) || 0) * quantityNum;

  useEffect(() => {
    setPerGramPrice(parseFloat(priceInput) || 0);
  }, [priceInput]);

  const sellerOptions = acceptedSellers.map((sellerData) => ({
    id: sellerData.seller.id,
    name: `${sellerData.seller.firstName || ''} ${sellerData.seller.businessName ? `(${sellerData.seller.businessName})` : ''}`.trim(),
    type: sellerData.matchedItem?.metalTypeDetails?.name || 'Unknown',
    matchedItem: sellerData.matchedItem,
    sellerDetails: sellerData.seller,
    fullData: sellerData
  }));

  const karatOptions = [
    { value: "24K", purity: "99.9%" },
    { value: "22K", purity: "91.6%" },
    { value: "18K", purity: "75%" },
    { value: "14K", purity: "58.5%" },
    { value: "10K", purity: "41.7%" }
  ];

  const centOptions = [
    { value: "0.50", purity: "0.50 ct" },
    { value: "0.75", purity: "0.75 ct" },
    { value: "1.00", purity: "1.00 ct" },
    { value: "1.50", purity: "1.50 ct" },
    { value: "2.00", purity: "2.00 ct" }
  ];

  // Seller Bill Calculations
  useEffect(() => {
    const purityNum = parseFloat(purityValue) || 0;
    const qty = quantityNum;
    const priceNum = parseFloat(priceInput) || 0;
    const wastageNum = parseFloat(wastageInput) || 0;
    const gstPercent = parseFloat(gstInput) || 0;

    const tunchSeller = purityNum + wastageNum;
    const fine = (tunchSeller * qty) / 100;
    const priceVal = priceNum * fine;
    const sellerSubtotal = priceVal + makingChargesAmount;
    const sellerGst = sellerSubtotal * (gstPercent / 100);
    const sellerTotal = sellerSubtotal + sellerGst;

    setOriginalTunchSeller(tunchSeller);
    setFineValue(fine);
    setCalculatedPriceValue(sellerSubtotal);
    setSellerGstAmount(sellerGst);
    setSellerGrandTotal(sellerTotal);
  }, [purityValue, quantityNum, priceInput, wastageInput, makingChargesAmount, gstInput]);

  // Buyer Bill Calculations
  useEffect(() => {
    const purityNum = parseFloat(purityValue) || 0;
    const wastageNum = parseFloat(wastageInput) || 0;
    const marginNum = parseFloat(commissionInput) || 0;
    const qty = quantityNum;
    const priceNum = parseFloat(priceInput) || 0;
    const gstPercent = parseFloat(gstInput) || 0;

    const tunchSeller = purityNum + wastageNum;
    const tunchBuyer = tunchSeller + marginNum;
    const fineBuyer = (tunchBuyer * qty) / 100;
    const buyerSubtotal = (priceNum * fineBuyer) + makingChargesAmount + marginMakingChargesAmount;
    const gstBuyer = buyerSubtotal * (gstPercent / 100);
    const buyerGrandTotal = buyerSubtotal + gstBuyer;
    const fineSeller = (tunchSeller * qty) / 100;
    const marginWastageProfit = (fineBuyer - fineSeller) * priceNum;

    setMarginMakingChargesProfit(marginMakingChargesAmount);
    setOriginalTunchBuyer(tunchBuyer);
    setFineValueBuyer(fineBuyer);
    setCalculatedPriceValueBuyer(buyerSubtotal - makingChargesAmount - marginMakingChargesAmount);
    setSubtotal(buyerSubtotal);
    setGstAmount(gstBuyer);
    setGrandTotal(buyerGrandTotal);
    setTotalPrice(buyerSubtotal);
    setCommissionAmount(marginWastageProfit);
  }, [purityValue, wastageInput, commissionInput, quantityNum, priceInput, gstInput, makingChargesAmount, marginMakingChargesAmount]);

  const totalAdminProfitValue = commissionAmount + marginMakingChargesProfit;
  const adminFine = perGramPrice > 0 ? totalAdminProfitValue / perGramPrice : 0;
  const originalTunchSellerValue = parseFloat(purityValue) || 0;

  const populateSellerData = (sellerData: any, forceDefaults = false) => {
    if (!sellerData) return;
    setCurrentSelectedSellerData(sellerData);
    setMatchedItemData(sellerData.matchedItem);
    const sourcePurity = getPurityFromSources(sellerData.requirement, selectedRow, sellerData.matchedItem);
    if (sellerData.matchedItem) {
      const matchedItem = sellerData.matchedItem;
      if (matchedItem.wastage) setWastageInput(matchedItem.wastage.toString());
      if (matchedItem.makingCharges && (!isMakingChargesEdited || forceDefaults)) setMakingChargesInput(matchedItem.makingCharges.toString());
      if (matchedItem.t2t !== undefined) setT2tOption(matchedItem.t2t ? "yes" : "no");

      if (matchedItem.difference !== undefined && matchedItem.difference !== null) {
        const diffValue = typeof matchedItem.difference === 'string'
          ? parseFloat(matchedItem.difference)
          : Number(matchedItem.difference);
        setDifference(isNaN(diffValue) ? 0 : diffValue);
      } else {
        setDifference(0);
      }

      if (matchedItem.karat && (!isKaratEdited || forceDefaults)) {
        setSelectedKarat(matchedItem.karat);
        const karatOption = karatOptions.find(k => k.value === matchedItem.karat);
        if (karatOption) {
          setPurityValue(karatOption.purity.replace('%', ''));
          setPurityInput(karatOption.purity.replace('%', ''));
        }
      } else if (sourcePurity && (!isPurityEdited || forceDefaults)) {
        setPurityValue(sourcePurity);
        setPurityInput(sourcePurity);
      }
      if (matchedItem.metalTypeDetails?.name) setSelectedMetalType(matchedItem.metalTypeDetails.name);
      if (matchedItem.category_name) setSelectedCategory(matchedItem.category_name);
      if (matchedItem.articleName) setSelectedArticle(matchedItem.articleName);
      if (matchedItem.weight) {
        const weightValue = matchedItem.weight.toString();
        setOriginalWeight(weightValue);
        if (matchedItem.uomDetails?.name) {
          const uomName = matchedItem.uomDetails.name;
          setOriginalUOM(uomName);
          setSelectedUOM(uomName);
          // Find UOM ID
          const foundUOM = uomOptions.find(u => 
            u.name === uomName || u.uomName === uomName || u.unit === uomName
          );
          if (foundUOM) {
            setSelectedUOMId(foundUOM.id);
          }
        }
      }
      if (matchedItem.uomDetails?.name) {
        const uomName = matchedItem.uomDetails.name;
        setSelectedUOM(uomName);
        setOriginalUOM(uomName);
        // Find UOM ID
        const foundUOM = uomOptions.find(u => 
          u.name === uomName || u.uomName === uomName || u.unit === uomName
        );
        if (foundUOM) {
          setSelectedUOMId(foundUOM.id);
        }
      }
    } else if (sourcePurity && (!isPurityEdited || forceDefaults)) {
      setPurityValue(sourcePurity);
      setPurityInput(sourcePurity);
    }
  };

  useEffect(() => {
    if (selectedRow) {
      const firstSeller = acceptedSellers[0];
      const requirementSource = firstSeller?.requirement || selectedRow;

      setPriceInput("");
      setPriceVisible(true);
      setPurityValue("");
      setPurityInput("");
      setSelectedKarat("");
      setSelectedCent("");
      setShowWastage(true);
      setT2tOption("no");
      setWastageInput("");
      setMakingChargesInput("");
      setCommissionInput("");
      setGstInput("");
      setUploadedImages([]);
      setImagePreviews([]);
      setPaymentMethod("COD");
      setMarginMakingChargesInput("");

      if (requirementSource.unique_number) setuniquenumber(requirementSource.unique_number);
      else if (acceptedSellers.length > 0 && acceptedSellers[0]?.requirement?.unique_number)
        setuniquenumber(acceptedSellers[0].requirement.unique_number);
      setIsPurityEdited(false);
      setIsKaratEdited(false);
      setIsMakingChargesEdited(false);
      const metalName = getMetalName(requirementSource.MetalTypeId || selectedRow.MetalTypeId);
      setSelectedMetalType(metalName);
      setSelectedCategory(requirementSource.category_name || selectedRow.category_name || "");
      setSelectedArticle(requirementSource.article_name || selectedRow.article_name || "");
      const weightValue = requirementSource.weight?.toString() || requirementSource.quantity?.toString() || "";
      setOriginalWeight(weightValue);
      const uomName = requirementSource.uomDetails?.name || requirementSource.UomName || selectedRow.uomDetails?.name;
      if (uomName) {
        setSelectedUOM(uomName);
        setOriginalUOM(uomName);
        // Find UOM ID
        const foundUOM = uomOptions.find(u => 
          u.name === uomName || u.uomName === uomName || u.unit === uomName
        );
        if (foundUOM) {
          setSelectedUOMId(foundUOM.id);
        }
      } else if (uomOptions.length > 0) {
        const defaultUOM = uomOptions[0]?.name || 'g';
        setSelectedUOM(defaultUOM);
        setOriginalUOM(defaultUOM);
        setSelectedUOMId(uomOptions[0]?.id || 1);
      }

      const initialQty = requirementSource.quantity?.toString() || selectedRow.quantity?.toString() || "1";
      setEditableQuantity(initialQty);

      if (acceptedSellers.length > 0) {
        const sellerName = `${firstSeller.seller.firstName || ''} ${firstSeller.seller.businessName ? `(${firstSeller.seller.businessName})` : ''}`.trim();
        setSelectedMetalSeller(sellerName);
        setSelectedSellerId(firstSeller.seller.id);
        populateSellerData(firstSeller, true);
      } else {
        setSelectedMetalSeller("");
        setSelectedSellerId(null);
        setMatchedItemData(null);
        setCurrentSelectedSellerData(null);
        setWastageInput("");
        setMakingChargesInput("");
        setCommissionInput("");
        setGstInput("");
        setMarginMakingChargesInput("");
      }
    }
  }, [selectedRow, getMetalName, acceptedSellers, uomOptions]);

  // Purity label effect
  useEffect(() => {
    if (!matchedItemData && !isPurityEdited) {
      setPurityValue("");
      setPurityInput("");
      setSelectedKarat("");
      setSelectedCent("");
    }
    setShowWastage(true);
    if (selectedRow && selectedMetalType) {
      const apiPurity = getPurityFromSources(currentSelectedSellerData?.requirement, selectedRow, currentSelectedSellerData?.matchedItem);
      const purityFromRow = apiPurity || getPurityLabel(selectedRow.MetalTypeId, selectedRow.category_name);
      switch (selectedMetalType) {
        case "Silver":
          setPurityLabel("Original Tunch Seller");
          if (!isPurityEdited) { setPurityValue(purityFromRow && purityFromRow !== "-" ? purityFromRow : "999"); setPurityInput(purityFromRow && purityFromRow !== "-" ? purityFromRow : "999"); }
          setShowWastage(true);
          break;
        case "Gold":
          setPurityLabel("Karat");
          if (purityFromRow && purityFromRow !== "-" && !matchedItemData && !isKaratEdited) {
            setSelectedKarat(purityFromRow);
            const karatOption = karatOptions.find(k => k.value === purityFromRow);
            if (karatOption) {
              setPurityValue(karatOption.purity.replace('%', ''));
              setPurityInput(karatOption.purity.replace('%', ''));
            }
          }
          setShowWastage(true);
          break;
        case "Diamond":
          setPurityLabel("Cent");
          setShowWastage(false);
          break;
        case "Platinum":
        case "Palladium":
          setPurityLabel("Purity (950)");
          if (!matchedItemData && !isPurityEdited) { setPurityValue("950"); setPurityInput("950"); }
          setShowWastage(true);
          break;
        default:
          setPurityLabel("Purity");
          if (!matchedItemData && !isPurityEdited) { setPurityValue(""); setPurityInput(""); }
          setShowWastage(true);
      }
    } else {
      switch (selectedMetalType) {
        case "Silver":
          setPurityLabel("Purity (999)");
          if (!isPurityEdited) {
            const apiPurity = getPurityFromSources(currentSelectedSellerData?.requirement, selectedRow, currentSelectedSellerData?.matchedItem);
            setPurityValue(apiPurity || "999");
            setPurityInput(apiPurity || "999");
          }
          setShowWastage(true);
          break;
        case "Gold":
          setPurityLabel("Karat");
          setShowWastage(true);
          break;
        case "Diamond":
          setPurityLabel("Cent");
          setShowWastage(false);
          break;
        case "Platinum":
        case "Palladium":
          setPurityLabel("Purity (950)");
          if (!matchedItemData && !isPurityEdited) { setPurityValue("950"); setPurityInput("950"); }
          setShowWastage(true);
          break;
        default:
          setPurityLabel("Purity");
          if (!matchedItemData && !isPurityEdited) { setPurityValue(""); setPurityInput(""); }
          setShowWastage(true);
      }
    }
  }, [selectedMetalType, selectedRow, getPurityLabel, matchedItemData, currentSelectedSellerData, isPurityEdited, isKaratEdited]);

  const handleKaratSelect = (karat: string, purity: string) => {
    setSelectedKarat(karat);
    setPurityValue(purity.replace('%', ''));
    setPurityInput(purity.replace('%', ''));
    setIsKaratEdited(true);
    setIsPurityEdited(true);
    setShowKaratDropdown(false);
  };

  const handleCentSelect = (cent: string, purity: string) => {
    setSelectedCent(cent);
    setPurityValue(purity.replace(' ct', ''));
    setPurityInput(purity.replace(' ct', ''));
    setIsPurityEdited(true);
    setShowCentDropdown(false);
  };

  const handlePurityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPurityInput(e.target.value);
    setPurityValue(e.target.value);
    setIsPurityEdited(true);
  };

  const handleT2tOptionChange = (option: string) => {
    setT2tOption(option);
    if (option === "no") {
      setUploadedImages([]);
      setImagePreviews([]);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadedImages(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreviews(prev => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendToCustomer = async () => {
    try {
      if (!selectedSellerId) { toast.error("Please select a seller"); return; }
      if (!priceInput || parseFloat(priceInput) <= 0) { toast.error("Please enter a valid price"); return; }
      if (!purityValue) { toast.error("Please enter purity value"); return; }

      const categoryId = selectedRow?.categoryId || 0;
      const articleId = selectedRow?.articleId || 0;
      // Use selectedUOMId if available, fallback to original
      const uomId = selectedUOMId || selectedRow?.uomDetails?.id || selectedRow?.UomId || 1;
      const metalTypeId = selectedRow?.MetalTypeId || 0;
      const karatValue = selectedKarat ? parseFloat(selectedKarat.replace('K', '')) : 0;
      const weight = 0;
      const quantity = quantityNum;
      const wastagePercent = parseFloat(wastageInput) || 0;
      const makingCharges = parseFloat(makingChargesInput) || 0;
      const commissionPercent = parseFloat(commissionInput) || 0;
      const gstPercent = parseFloat(gstInput) || 0;
      const marginMakingCharges = parseFloat(marginMakingChargesInput) || 0;
      const MetalSilverPay = subtotal / (parseFloat(priceInput) || 0);
      const amountPerGram = perGramPrice;
      const amountPerQuantity = totalPrice;
      const buyerInfo = {
        id: selectedRow?.userId || selectedRow?.buyerDetails?.id || 0,
        name: selectedRow?.buyerDetails?.businessName || selectedRow?.buyerDetails?.firstName || getBuyerInfo(selectedRow?.userId).name || "Unknown Buyer",
        phone: selectedRow?.buyerDetails?.mobileNumber || getBuyerInfo(selectedRow?.userId).phone || "-"
      };
      const sellerInfo = {
        id: selectedSellerId,
        name: currentSelectedSellerData?.seller?.firstName || (currentSelectedSellerData?.seller?.businessName ? `(${currentSelectedSellerData.seller.businessName})` : '') || selectedMetalSeller || "Unknown Seller",
        businessName: currentSelectedSellerData?.seller?.businessName || null,
        phone: currentSelectedSellerData?.seller?.mobileNumber || "-"
      };

      // Use selectedUOM for the order
      const finalUOM = selectedUOM || originalUOM || 'g';

      const requestBody = {
        matchSellerId: selectedSellerId,
        buyerId: buyerInfo.id,
        buyerName: buyerInfo.name,
        buyerPhone: buyerInfo.phone,
        perGmPrice: priceInput,
        sellerId: sellerInfo.id,
        sellerName: sellerInfo.name,
        sellerBusinessName: sellerInfo.businessName,
        sellerPhone: sellerInfo.phone,
        categoryId, 
        articleId, 
        uomId, // Now using selectedUOMId
        metalTypeId,
        purity: selectedKarat || purityInput,
        karat: karatValue,
        carat: 0,
        centValue: parseFloat(selectedCent) || 0,
        weight, 
        originalWeight, 
        quantity, 
        originalUOM: finalUOM,
        wastagePercent,
        makingChargesPerGram: makingCharges,
        WastageMakingChargesPerGram: marginMakingCharges,
        commissionPercent, 
        gstPercent,
        gstAmount: gstAmount,
        PaymentMethod: paymentMethod,
        amountPerGram, 
        amountPerQuantity,
        priceVisibility: priceVisible,
        buyerFine: fineValueBuyer,
        sellerfine: fineValue,
        buyerPurity: originalTunchBuyer,
        sellerPurity: originalTunchSeller,
        buyertotalAmount: subtotal,
        buyertotalAmmoumtGst: grandTotal,
        sellertotalAmount: calculatedPriceValue,
        sellerGstAmount: sellerGstAmount,
        sellerGrandTotal: sellerGrandTotal,
        buyerPrice: calculatedPriceValueBuyer,
        sellerPrice: calculatedPriceValue,
        unique_number: uniquenumber,
        MetalSilverPay,
        marginWastageProfit: commissionAmount,
        marginMakingChargesProfit: marginMakingChargesProfit,
        totalAdminProfit: commissionAmount + marginMakingChargesProfit,
      };

      const token = localStorage.getItem('token');
      if (!token) { toast.error("Authentication token not found. Please login again."); return; }
      const response = await fetch('https://api.sonachandiwale.com/api/v1/admin/create-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestBody)
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Invoice created successfully!");
        onClose();
      } else {
        toast.error(data.message || "Failed to create invoice");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("An error occurred while creating invoice. Please check the console for details.");
    }
  };

  const handleSellerSelect = (seller: any) => {
    setSelectedMetalSeller(seller.name);
    setSelectedSellerId(seller.id);
    setIsPurityEdited(false);
    setIsKaratEdited(false);
    setIsMakingChargesEdited(false);
    populateSellerData(seller.fullData, true);
    setShowSellerDropdown(false);
  };

  if (!isOpen || !selectedRow) return null;

  const buyerName = selectedRow.buyerDetails?.businessName || selectedRow.buyerDetails?.firstName || getBuyerInfo(selectedRow.userId).name || "Unknown Buyer";
  const marginWastageValue = commissionAmount;
  const adminProfitValue = marginMakingChargesProfit;

  // Display UOM - use selectedUOM if available, fallback to originalUOM
  const displayUOM = selectedUOM || originalUOM || 'g';

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
    >
      <div
        className="invoice-modal"
        style={{
          maxWidth: '900px',
          width: '95%',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: "#0f172a",
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="invoice-card" style={{ padding: '24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #FBBF24', paddingBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calculator size={24} color="#FBBF24" />
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#fff' }}>Create Invoice - Deal Calculator</h2>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9CA3AF', padding: '0 8px' }}><X size={20} /></button>
          </div>
          <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#FBBF24', fontWeight: '500' }}>REQ-{String(selectedRow.id).padStart(3, "0")} - {selectedRow.category_name} for {buyerName}</p>

          {/* Product Details Box */}
          <div style={{ background: "#233868", border: '1px solid #666', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#FBBF24', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '600', color: '#FFFFFF' }}>{buyerName?.charAt(0) || 'P'}</div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#fff' }}>{selectedRow.article_name || selectedRow.category_name || 'Product'}</h3>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}><span style={{ backgroundColor: '#fff', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: '#4B5563' }}>{getMetalName(selectedRow.MetalTypeId)}</span></div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', marginTop: '12px' }}>
              <div><div style={{ fontSize: '12px', color: '#fff', marginBottom: '4px' }}>Buyer</div><div style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>{buyerName}</div></div>
              <div><div style={{ fontSize: '12px', color: '#fff', marginBottom: '4px' }}>Type</div><div style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>{getMetalName(selectedRow.MetalTypeId)}</div></div>
              <div><div style={{ fontSize: '12px', color: '#fff', marginBottom: '4px' }}>Article</div><div style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>{selectedRow.article_name || selectedRow.category_name || '-'}</div></div>
              <div><div style={{ fontSize: '12px', color: '#fff', marginBottom: '4px' }}>Quantity</div><div style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>{selectedRow.quantity || 1}</div></div>
              <div>
                <div style={{ fontSize: '12px', color: '#fff', marginBottom: '4px' }}>Original Weight</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>
                  {originalWeight || "0"} {displayUOM}
                </div>
              </div>
            </div>
          </div>

          <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0', color: '#FBBF24', borderBottom: '1px solid #FBBF24', paddingBottom: '8px' }}>INVOICE DETAILS</h4>

          {/* First Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr', gap: '20px', marginBottom: '20px' }}>
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Matched Seller <span style={{ color: '#EF4444' }}>*</span>{acceptedSellers.length > 0 && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#10B981' }}>({acceptedSellers.length} Accepted)</span>}</label>
              <div onClick={() => { if (sellerOptions.length > 0) setShowSellerDropdown(!showSellerDropdown); }} style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: sellerOptions.length > 0 ? 'pointer' : 'not-allowed', backgroundColor: '#1F2937', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', color: selectedMetalSeller ? '#ffffff' : '#9CA3AF', opacity: sellerOptions.length > 0 ? 1 : 0.6 }}>
                <span>{selectedMetalSeller ? selectedMetalSeller : sellerOptions.length > 0 ? "Select Accepted Seller" : "No Accepted Sellers Available"}</span>
                {sellerOptions.length > 0 && <ChevronDown size={16} color="#9CA3AF" />}
              </div>
              {showSellerDropdown && sellerOptions.length > 0 && (
                <>
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setShowSellerDropdown(false)} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#354458', border: '1px solid #E5E7EB', borderRadius: '8px', marginTop: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                    {sellerOptions.map((seller) => (
                      <div key={seller.id} onClick={() => handleSellerSelect(seller)} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', backgroundColor: selectedSellerId === seller.id ? '#354458' : '#1F2937', borderBottom: '1px solid #F3F4F6', color: '#ffffff' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4B5563'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedSellerId === seller.id ? '#354458' : '#1F2937'}>
                        <div style={{ fontWeight: '500', color: '#FBBF24' }}>{seller.name}</div>
                        <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{seller.type} {seller.matchedItem ? '• Matched Item Available' : ''}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Metal Type <span style={{ color: '#EF4444' }}>*</span></label><div style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#2d3748', fontSize: '14px', color: '#9CA3AF', cursor: 'not-allowed' }}>{selectedMetalType || "Select Metal Type"}</div></div>
            
            {/* UOM Dropdown - Dynamic from API */}
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>
                Unit of Measurement <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <div
                onClick={() => setShowUOMDropdown(!showUOMDropdown)}
                style={{
                  padding: '10px 14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: '#1F2937',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '14px',
                  color: selectedUOM ? '#ffffff' : '#9CA3AF'
                }}
              >
                <span>{selectedUOM || "Select UOM"}</span>
                <ChevronDown size={16} color="#9CA3AF" />
              </div>

              {showUOMDropdown && (
                <>
                  <div
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                    onClick={() => setShowUOMDropdown(false)}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#354458',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      marginTop: '4px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    {uomOptions.length === 0 ? (
                      <div style={{ padding: '10px 14px', color: '#9CA3AF', fontSize: '14px' }}>
                        Loading UOMs...
                      </div>
                    ) : (
                      uomOptions.map((uom: any) => {
                        const uomName = uom.name || uom.uomName || uom.unit || `UOM ${uom.id}`;
                        return (
                          <div
                            key={uom.id}
                            onClick={() => handleUOMChange(uom)}
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: selectedUOM === uomName ? '#4B5563' : '#1F2937',
                              borderBottom: '1px solid #F3F4F6',
                              color: '#ffffff'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4B5563'}
                            onMouseLeave={(e) =>
                              e.currentTarget.style.backgroundColor =
                              selectedUOM === uomName ? '#4B5563' : '#1F2937'
                            }
                          >
                            {uomName}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Second Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr', gap: '20px', marginBottom: '24px' }}>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Current Metal Price (Per Gm) <span style={{ color: '#EF4444' }}>*</span></label><div style={{ display: 'flex', alignItems: 'center' }}><span style={{ padding: '10px 14px', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB', borderRight: 'none', borderRadius: '8px 0 0 8px', color: '#4B5563', fontSize: '14px', fontWeight: '500' }}>₹</span><input type="number" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} placeholder="Enter price" style={{ flex: 1, padding: '10px 14px', border: '1px solid #E5E7EB', borderLeft: 'none', borderRadius: '0 8px 8px 0', outline: 'none', fontSize: '14px', color: '#ffff', backgroundColor: '#1F2937' }} /></div></div>
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>{purityLabel} <span style={{ color: '#EF4444' }}>*</span></label>
              {selectedMetalType === "Gold" ? (
                <>
                  <div onClick={() => setShowKaratDropdown(!showKaratDropdown)} style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#1F2937', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', color: selectedKarat ? '#ffffff' : '#9CA3AF' }}><span>{selectedKarat || "Select Karat"}</span><ChevronDown size={16} color="#9CA3AF" /></div>
                  {showKaratDropdown && (<><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setShowKaratDropdown(false)} /><div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#354458', border: '1px solid #E5E7EB', borderRadius: '8px', marginTop: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>{karatOptions.map((option) => (<div key={option.value} onClick={() => handleKaratSelect(option.value, option.purity)} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', backgroundColor: selectedKarat === option.value ? '#4B5563' : '#1F2937', borderBottom: '1px solid #F3F4F6', color: '#ffffff', display: 'flex', justifyContent: 'space-between' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4B5563'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedKarat === option.value ? '#4B5563' : '#1F2937'}><span>{option.value}</span><span style={{ color: '#FBBF24' }}>{option.purity}</span></div>))}</div></>)}
                </>
              ) : selectedMetalType === "Diamond" ? (
                <>
                  <div onClick={() => setShowCentDropdown(!showCentDropdown)} style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#1F2937', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', color: selectedCent ? '#ffffff' : '#9CA3AF' }}><span>{selectedCent ? `${selectedCent} ct` : "Select Cent"}</span><ChevronDown size={16} color="#9CA3AF" /></div>
                  {showCentDropdown && (<><div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setShowCentDropdown(false)} /><div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#354458', border: '1px solid #E5E7EB', borderRadius: '8px', marginTop: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>{centOptions.map((option) => (<div key={option.value} onClick={() => handleCentSelect(option.value, option.purity)} style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', backgroundColor: selectedCent === option.value ? '#4B5563' : '#1F2937', borderBottom: '1px solid #F3F4F6', color: '#ffffff' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4B5563'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedCent === option.value ? '#4B5563' : '#1F2937'}>{option.purity}</div>))}</div></>)}
                </>
              ) : (<input type="text" value={purityInput} onChange={handlePurityInputChange} placeholder={`Enter ${purityLabel.toLowerCase()}`} style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', color: '#ffff', backgroundColor: '#1F2937' }} />)}
            </div>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Category <span style={{ color: '#EF4444' }}>*</span></label><div style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#2d3748', fontSize: '14px', color: '#9CA3AF', cursor: 'not-allowed' }}>{selectedCategory || "Select Category"}</div></div>
          </div>

          {/* Third Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr', gap: '20px', marginBottom: '24px' }}>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Article <span style={{ color: '#EF4444' }}>*</span></label><div style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#2d3748', fontSize: '14px', color: '#9CA3AF', cursor: 'not-allowed' }}>{selectedArticle || (selectedCategory ? "Select Article" : "Select category first")}</div></div>

            <div>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#4B5563", marginBottom: "6px" }}>Purchase (Qty/gm) <span style={{ color: "#EF4444" }}>*</span></label>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <input
                  type="number"
                  value={editableQuantity}
                  onChange={(e) => setEditableQuantity(e.target.value)}
                  style={{ flex: 1, padding: "10px 14px", border: "1px solid #E5E7EB", borderRadius: "8px", fontSize: "16px", color: "#111827", fontWeight: "600", outline: "none" }}
                  placeholder="Enter quantity"
                />
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#9CA3AF" }}>
                  (Original Quantity: {selectedRow.quantity || 1})
                </div>
              </div>
            </div>

            <div style={{ background: "#1a2538", border: "1px solid #b8860b", borderRadius: "12px", padding: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ background: "#b8860b", color: "#000", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" }}>T2T</span>
                  <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#ffffff" }}>Tunch To Tunch</h4>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", background: "#0f172a", padding: "5px", borderRadius: "8px" }}>
                <label style={{ display: "flex", gap: "8px", color: "#9CA3AF", fontSize: "14px", cursor: "pointer" }}>
                  <input type="radio" name="t2tOption" value="yes" checked={t2tOption === "yes"} onChange={() => handleT2tOptionChange("yes")} style={{ accentColor: "#b8860b", width: "16px", height: "16px", cursor: "pointer" }} /> Yes
                </label>
                <label style={{ display: "flex", gap: "8px", color: "#9CA3AF", fontSize: "14px", cursor: "pointer" }}>
                  <input type="radio" name="t2tOption" value="no" checked={t2tOption === "no"} onChange={() => handleT2tOptionChange("no")} style={{ accentColor: "#b8860b", width: "16px", height: "16px", cursor: "pointer" }} /> No
                </label>
              </div>
              {t2tOption === "no" && (
                <div style={{ background: "#0f172a", border: "1px solid #2a3548", borderRadius: "8px", padding: "10px", marginTop: "10px" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#9CA3AF", marginBottom: "8px" }}>Upload Test Reports / Images</label>
                    <input type="file" multiple accept="image/*,.pdf" onChange={handleImageUpload} style={{ display: 'none' }} id="file-upload" />
                    <label htmlFor="file-upload" style={{ border: "2px dashed #b8860b", borderRadius: "8px", padding: "20px", textAlign: "center", background: "#1a2538", cursor: "pointer", display: "block" }}>
                      <div style={{ color: "#b8860b", fontSize: "32px", marginBottom: "8px" }}>📎</div>
                      <div style={{ color: "#9CA3AF", fontSize: "14px", marginBottom: "4px" }}>Drag & drop files here or click to browse</div>
                      <div style={{ color: "#9CA3AF", fontSize: "12px" }}>Supported formats: JPG, PNG, PDF (Max 10MB each)</div>
                    </label>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#9CA3AF", marginBottom: "8px" }}>Difference (gm)</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <button type="button" onClick={decreaseDifference} style={{ width: "36px", height: "36px", borderRadius: "8px", border: "1px solid #b8860b", background: "#1a2538", color: "#b8860b", fontSize: "20px", cursor: "pointer" }}>-</button>
                      <input type="number" step="0.1" value={difference} readOnly style={{ flex: 1, height: "36px", borderRadius: "8px", border: "1px solid #2a3548", background: "#111827", color: "#ffffff", textAlign: "center", fontSize: "14px" }} />
                      <button type="button" onClick={increaseDifference} style={{ width: "36px", height: "36px", borderRadius: "8px", border: "1px solid #b8860b", background: "#1a2538", color: "#b8860b", fontSize: "20px", cursor: "pointer" }}>+</button>
                    </div>
                  </div>
                  {imagePreviews.length > 0 && (
                    <div style={{ marginTop: "16px" }}>
                      <div style={{ fontSize: "14px", color: "#ffffff", marginBottom: "12px", fontWeight: "500" }}>Uploaded Files ({imagePreviews.length})</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
                        {imagePreviews.map((preview, index) => (
                          <div key={index} style={{ position: "relative", borderRadius: "8px", overflow: "hidden", border: "1px solid #2a3548", aspectRatio: "1" }}>
                            <img src={preview} alt={`Preview ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <button onClick={() => removeImage(index)} style={{ position: "absolute", top: "4px", right: "4px", background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#ffffff" }}>×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0', color: '#FBBF24', borderBottom: '1px solid #FBBF24', paddingBottom: '8px' }}>CALCULATION PARAMETERS</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '16px' }}>
            {showWastage && (<div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Wastage (%)</label><input type="number" value={wastageInput} onChange={(e) => setWastageInput(e.target.value)} placeholder="Enter wastage %" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', backgroundColor: '#1F2937', color: '#ffffff' }} /></div>)}
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Making Charges(Per/Gm) <span style={{ color: '#10B981', fontSize: '12px' }}>(Adds to Seller)</span></label><input type="number" value={makingChargesInput} onChange={(e) => { setMakingChargesInput(e.target.value); setIsMakingChargesEdited(true); }} placeholder="Enter making charges" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', backgroundColor: '#1F2937', color: '#ffffff' }} /></div>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>UP Wastage Margin (%) <span style={{ color: '#EF4444' }}>*</span></label><input type="number" value={commissionInput} onChange={(e) => setCommissionInput(e.target.value)} placeholder="Enter commission %" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', backgroundColor: '#1F2937', color: '#ffffff' }} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>GST (%) <span style={{ color: '#EF4444' }}>*</span></label><input type="number" value={gstInput} onChange={(e) => setGstInput(e.target.value)} placeholder="Enter GST %" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', backgroundColor: '#1F2937', color: '#ffffff' }} /></div>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Payment Method</label><select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', backgroundColor: '#1F2937', color: '#ffffff', cursor: 'pointer' }}><option value="COD">COD</option><option value="UPI">UPI</option><option value="BANK TRANSFER">BANK TRANSFER</option><option value="METAL PAY">METAL PAY</option></select></div>
            <div><label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4B5563', marginBottom: '6px' }}>Margin Making Charges (Per Gm) <span style={{ color: '#FBBF24', fontSize: '12px' }}>(Adds to Buyer & Admin)</span></label><input type="number" value={marginMakingChargesInput} onChange={(e) => setMarginMakingChargesInput(e.target.value)} placeholder="Enter margin making charges" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', outline: 'none', fontSize: '14px', backgroundColor: '#1F2937', color: '#ffffff' }} /></div>
          </div>

          {/* Seller Invoice */}
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}><span style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>Seller Invoice</span></div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Original Tunch Seller</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(originalTunchSellerValue)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Tunch + Wastage to seller</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(originalTunchSeller)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Purchasing Quantity (No. of pieces)</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{quantityNum}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Fine</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(fineValue)} gm</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Price (without Making charge)</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(fineValue * perGramPrice)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Making Charges</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(makingChargesAmount)}</span></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '2px solid #FBBF24', marginBottom: '8px' }}><span style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff' }}>Subtotal</span><span style={{ fontSize: '15px', fontWeight: '600', color: '#FBBF24' }}>{formatINR(calculatedPriceValue)}</span></div>
            <div style={{ marginBottom: '12px' }}><div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>GST {gstInput ? `(${gstInput}%)` : ''}</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(sellerGstAmount)}</span></div></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0 0', borderTop: '2px solid #FBBF24' }}><span style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>Grand Total</span><span style={{ fontSize: '20px', fontWeight: '700', color: '#FBBF24' }}>{formatINR(sellerGrandTotal)}</span></div>
          </div>

          {/* Buyer Invoice */}
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}><span style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>Buyer Invoice</span></div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Original Tunch to buyer</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(originalTunchBuyer)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Purchasing Quantity (No. of pieces)</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{quantityNum}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Fine</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(fineValueBuyer)} gm</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Price (without Making charge)</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(fineValueBuyer * perGramPrice)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Making Charges</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(makingChargesAmount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>Margin Making Charges</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(marginMakingChargesAmount)}</span></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '2px solid #FBBF24', marginBottom: '8px' }}><span style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff' }}>Subtotal</span><span style={{ fontSize: '15px', fontWeight: '600', color: '#FBBF24' }}>{formatINR(subtotal)}</span></div>
            <div style={{ marginBottom: '12px' }}><div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}><span style={{ fontSize: '14px', color: '#9CA3AF' }}>GST {gstInput ? `(${gstInput}%)` : ''}</span><span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(gstAmount)}</span></div></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0 0', borderTop: '2px solid #FBBF24' }}><span style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>Grand Total</span><span style={{ fontSize: '20px', fontWeight: '700', color: '#FBBF24' }}>{formatINR(grandTotal)}</span></div>
          </div>

          {/* Admin Profit - ONLY for Super Admin */}
          {isSuperAdmin && (
            <div style={{ backgroundColor: '#1F2937', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#ffffff' }}>Admin Profit</span>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}>
                  <span style={{ fontSize: '14px', color: '#9CA3AF' }}>Margin Wastage Profit</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(marginWastageValue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}>
                  <span style={{ fontSize: '14px', color: '#9CA3AF' }}>Margin Making Charges Profit</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(adminProfitValue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #374151' }}>
                  <span style={{ fontSize: '14px', color: '#9CA3AF' }}>Total Admin Profit</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#FBBF24' }}>{formatINR(totalAdminProfitValue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ fontSize: '14px', color: '#9CA3AF' }}>Admin Fine</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(adminFine)} gm</span>
                </div>
              </div>
            </div>
          )}

          {/* Visibility Toggle & Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1F2937', border: '1px solid #FBBF24', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><Eye size={20} color="#FBBF24" /><div><div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>Price Visibility</div><div style={{ fontSize: '12px', color: '#9CA3AF' }}>Customer will see the price breakdown</div></div></div>
            <div style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }} onClick={() => setPriceVisible(!priceVisible)}><div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: priceVisible ? '#FBBF24' : '#374151', borderRadius: '24px', transition: '0.4s' }}><div style={{ position: 'absolute', height: '20px', width: '20px', left: priceVisible ? '22px' : '2px', bottom: '2px', backgroundColor: 'white', borderRadius: '50%', transition: '0.4s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /></div></div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '10px 24px', border: '1px solid #374151', borderRadius: '8px', backgroundColor: '#1F2937', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSendToCustomer} style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', backgroundColor: '#FBBF24', color: '#000000', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Send to Customer</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealCalculatorModal;